import {
  Fragment,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import '@Quarantine/RiskAI/index.less';
import {
  Checkbox,
  Flex,
  Option,
  Select,
  Switch,
  Stack,
  Text,
  Truncate,
  useColorMode,
  useColorStyle,
  TonicProps,
  TableBody,
  Table,
  TableRow,
  TableCell,
  TableHeader,
  Box,
} from '@tonic-one/react';
import { useTranslation } from 'react-i18next';
import {
  FormInput,
  FormLabel,
  FormTextarea,
} from 'src/components/Common/FormComponent';
import { Info } from 'src/components/Common/Info';
import {
  ActionData,
  ActionEnum,
  AsActionKeys,
} from 'src/pages/Policy/utils/types';
import { useWhyDidYouUpdate } from 'src/utils/hooks';

import useActionNotificationStyle from './style';
import { templateSelector, usePolicyContext } from '@Policy/store';
import { PolicyModeEnum, PolicyTypeEnum } from '@Policy/utils/constants';
import { actionTranslateMap } from './shared';
import {
  createColumnHelper,
  useReactTable,
  getCoreRowModel,
  flexRender,
  Row,
} from '@tanstack/react-table';
import TextEllipsis from '@Components/Common/Tooltip/TextEllipsis';
import { PolicySubTypeEnum } from '../../../../../types';

const ROW_HEIGHT = 48;

export const ActionTableHeader = (
  props: { children: ReactNode } & TonicProps
) => {
  const { children, ...tonicProps } = props;
  const { actionNameStyle } = useActionNotificationStyle();
  return (
    <Truncate {...actionNameStyle} {...tonicProps}>
      {children}
    </Truncate>
  );
};

function Actions({
  actionData,
  disabledAll,
  wrapThreatType = false,
}: {
  actionData: ActionData[];
  disabledAll: boolean;
  wrapThreatType?: boolean;
}) {
  const { t } = useTranslation();
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const prefixRef = useRef(Math.random().toString(36).slice(4));
  const contextStore = usePolicyContext();
  const policySelector = templateSelector(contextStore);
  const policyType = policySelector((s) => s.policyType);
  const policyMode = policySelector((s) => s.policyMode);
  const policySubType = policySelector((s) => s.policySubType);

  useWhyDidYouUpdate('Actions', { actionData });

  const modifiedActionData = useMemo(
    () =>
      actionData.map((item) => {
        const newItem = { ...item };
        // for gmail inline inbound policy, block sender action "Move to spam" action is a error statue
        // if action has been "Move to spam", keep the previously set data options
        if (
          policyType === PolicyTypeEnum.GmailInline &&
          policySubType === PolicySubTypeEnum.inbound
        ) {
          if (
            newItem.actionKey === AsActionKeys.asActionBlockedSender &&
            newItem.action === ActionEnum.moveToSpam
          ) {
            newItem.actions.push(ActionEnum.moveToSpam);
          }
        }

        if (
          policyType === PolicyTypeEnum.GmailInline ||
          policyType === PolicyTypeEnum.ExchangeInline
        ) {
          if (newItem.action !== ActionEnum.delete) {
            newItem.actions = newItem.actions.filter(
              (action) => action !== ActionEnum.delete
            );
          }
        }

        //https://trendmicro.atlassian.net/browse/CAS-2583
        if (
          [
            PolicyModeEnum.DefaultXDRMonitorPolicy,
            PolicyModeEnum.DefaultMonitorPolicy,
          ].includes(policyMode) &&
          [
            PolicyTypeEnum.Exchange,
            PolicyTypeEnum.ExchangeInline,
            PolicyTypeEnum.GmailInline,
            PolicyTypeEnum.Gmail,
          ].includes(policyType)
        ) {
          if (newItem.actionKey === AsActionKeys.asActionBlockedSender) {
            newItem.actions.push(ActionEnum.pass);
            newItem.action = ActionEnum.pass;
          }
          if (newItem.actionKey === 'wtpActionBlockedUrl') {
            newItem.actions.push(ActionEnum.pass);
            newItem.action = ActionEnum.pass;
          }
        }
        return newItem;
      }),
    [actionData, policyType, policySubType, policyMode]
  );

  const actionHandle = useCallback(
    (item: ActionData) => {
      //no action downgraded to label email for gmail
      if (policyType === PolicyTypeEnum.Gmail) {
        if (
          item.action === ActionEnum.quarantine &&
          !item.actions.includes(item.action)
        ) {
          return ActionEnum.labelEmail;
        }
      }
      return item.action;
    },
    [policyType]
  );

  const columns = useMemo(() => {
    const colHelper = createColumnHelper<ActionData>();
    return [
      colHelper.accessor('threatType', {
        header: () => (
          <TextEllipsis>{t('policy.label.threatType')}</TextEllipsis>
        ),
        cell: (cell) => (
          <ActionName
            item={cell.row.original}
            wrapThreatType={wrapThreatType}
          />
        ),
        size: 200,
      }),
      colHelper.accessor('action', {
        header: () => <TextEllipsis>{t('policy.label.action')}</TextEllipsis>,
        cell: (cell) => (
          <ActionSelect
            item={cell.row.original}
            actionHandle={actionHandle}
            disabledAll={disabledAll}
          />
        ),
        size: 360,
      }),
      colHelper.accessor('notifyType', {
        header: () => (
          <TextEllipsis>{t('policy.label.notification')}</TextEllipsis>
        ),
        cell: (cell) => <ActionNotification item={cell.row.original} />,
        size: 180,
      }),
      colHelper.accessor('otherSettings', {
        header: () => (
          <TextEllipsis>{t('policy.label.otherSettings')}</TextEllipsis>
        ),
        cell: (cell) => <ActionOtherSettings item={cell.row.original} />,
        meta: {
          customColumnStyle: {
            flex: '1',
          },
        },
      }),
    ];
  }, [actionHandle, disabledAll, t, wrapThreatType]);

  const renderExpandedRow = useCallback(
    (row: Row<ActionData>) => {
      const item = row.original;
      const tableBorderColor = {
        dark: 'gray:70',
        light: 'gray:30',
      }[colorMode];
      const dividerColor = {
        dark: 'gray:60',
        light: 'gray:30',
      }[colorMode];

      return (
        <Flex
          borderBottom={1}
          borderBottomColor={tableBorderColor}
          minHeight={ROW_HEIGHT}
        >
          <Box width="6x" borderRight={2} borderRightColor={dividerColor} />
          {item.action === ActionEnum.tagSubject && (
            <Flex alignItems="center" py="2x" px="3x" width="90%">
              <FormLabel label={t('policy.actions.tagSubject')} width="152px" />
              <TagSubjectInput item={item} />
            </Flex>
          )}
          {item.action === ActionEnum.addDisclaimer && (
            <Flex alignItems="start" py="2x" px="3x" width="90%">
              <FormLabel
                label={t('policy.actions.addDisclaimer')}
                width="152px"
              />
              <DisclaimerInput item={item} />
            </Flex>
          )}
        </Flex>
      );
    },
    [colorMode, t]
  );

  const table = useReactTable({
    data: modifiedActionData,
    columns,
    getRowCanExpand: () => true,
    getIsRowExpanded: (row: Row<ActionData>) =>
      [ActionEnum.tagSubject, ActionEnum.addDisclaimer].includes(
        row.original.action
      ),
    getCoreRowModel: getCoreRowModel(),
  });

  // Don't show the table if there is only one action
  if (actionData.length === 1) {
    const item = actionData[0];
    return (
      <Stack gap="3x" key={`action-${prefixRef.current}`}>
        <ActionName item={item} wrapThreatType={wrapThreatType} />
        <Flex alignItems="center">
          <FormLabel label={t('policy.label.action')} />
          <ActionSelect
            item={item}
            actionHandle={actionHandle}
            disabledAll={disabledAll}
          />
        </Flex>
        {item.action === ActionEnum.tagSubject && (
          <Flex alignItems="center" width="90%">
            <FormLabel label={t('policy.actions.tagSubject')} />
            <TagSubjectInput item={item} />
          </Flex>
        )}
        {item.action === ActionEnum.addDisclaimer && (
          <Flex alignItems="center">
            <FormLabel label={t('policy.actions.addDisclaimer')} />
            <DisclaimerInput item={item} />
          </Flex>
        )}
        <Flex alignItems="center">
          <FormLabel label={t('policy.label.notification')} />
          <ActionNotification item={item} />
        </Flex>
        {item.otherSettings && (
          <Flex alignItems="center">
            <FormLabel label={t('policy.label.otherSettings')} />
            <ActionOtherSettings item={item} />
          </Flex>
        )}
      </Stack>
    );
  }

  return (
    <Table layout="flexbox" minWidth="100%" height="100%">
      <TableHeader width="100%">
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow width="100%" key={`header-group-${prefixRef.current}`}>
            {headerGroup.headers.map((header) => (
              <TableCell
                key={`header-${header.id}`}
                width={header.getSize()}
                minWidth={header.column.columnDef.minSize}
                {...header.column.columnDef.meta?.customColumnStyle}
              >
                {header.isPlaceholder
                  ? null
                  : flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody height="100%" width="100%">
        {table.getRowModel().rows.map((row) => (
          <Fragment key={`row-${prefixRef.current}`}>
            <TableRow
              width="100%"
              minHeight={ROW_HEIGHT}
              _hover={{
                backgroundColor: colorStyle.background.selected,
              }}
              _selected={{
                backgroundColor: colorStyle.background.selected,
              }}
            >
              {row.getVisibleCells().map((cell) => (
                <TableCell
                  key={`cell-${cell.id}`}
                  display="flex"
                  alignItems="center"
                  width={cell.column.getSize()}
                  minWidth={cell.column.columnDef.minSize}
                  {...cell.column.columnDef.meta?.customColumnStyle}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
            {row.getIsExpanded() && renderExpandedRow(row)}
          </Fragment>
        ))}
      </TableBody>
    </Table>
  );
}

export default Actions;

const ActionName = (props: { item: ActionData; wrapThreatType?: boolean }) => {
  const { item, wrapThreatType = false } = props;

  return (
    <>
      <Flex
        alignItems={wrapThreatType ? 'flex-start' : 'center'}
        overflow={wrapThreatType ? 'visible' : 'hidden'}
        width={wrapThreatType ? '100%' : undefined}
      >
        {wrapThreatType ? (
          <Text
            flex="1"
            minWidth={0}
            fontSize="16px"
            whiteSpace="normal"
            overflowWrap="anywhere"
          >
            {item.threatType}
          </Text>
        ) : (
          <TextEllipsis
            contentProps={{ fontSize: '16px', alignItems: 'center' }}
          >
            {item.threatType}
          </TextEllipsis>
        )}
        {item.info && <Info ml="2x" content={`${item.info}`} />}
      </Flex>
      {!!item.threatDescription && (
        <Text size="sm" mt="4x">
          {item.threatDescription}
        </Text>
      )}
    </>
  );
};

const ActionSelect = (props: {
  item: ActionData;
  actionHandle: (item: ActionData) => ActionEnum;
  disabledAll: boolean;
}) => {
  const { item, actionHandle, disabledAll } = props;
  const { t } = useTranslation();
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });

  useEffect(() => {
    if (!item.actions.includes(item.action)) {
      item.handler({
        [item.actionKey]: item.actions[0],
      });
    }
  }, [item]);

  return (
    <Stack gap="2x">
      <Flex alignItems="center">
        <Flex direction="column">
          <Select
            value={actionHandle(item)}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
              if (item.handler) {
                const action = Number(e.target.value);
                item.handler({
                  [item.actionKey]: action,
                });
                if (action === ActionEnum.passWithoutLogging) {
                  item.handler({
                    [item.notifyTypeKey]: 0,
                  });
                }
                if (action === ActionEnum.passwordQuarantine) {
                  item.handler({
                    [item.notifyTypeKey]: 1,
                  });
                }
              }
            }}
            width="237px"
            disabled={
              disabledAll ||
              (typeof item.actionTypeDisabled !== 'undefined'
                ? !!item.actionTypeDisabled
                : false)
            }
          >
            {item.actions.map((value: ActionEnum) => {
              return (
                <Option
                  key={`${item.threatType}-action-${value}`}
                  value={value}
                >
                  {t(actionTranslateMap[value])}
                </Option>
              );
            })}
          </Select>
        </Flex>
        <Flex ml="2x">
          {item.threatType === t('policy.threatType.suspiciousSender') && (
            <Info content={t('policy.threatType.suspiciousSender.info')} />
          )}
          {item.action === ActionEnum.passwordQuarantine && (
            <Info content={t('policy.action.passwordQuarantineTooltip')} />
          )}
        </Flex>
      </Flex>
      {item.action === ActionEnum.passwordQuarantine && (
        <Flex>
          <Text size="sm" color={colorStyle.color.secondary} width="340px">
            {t('policy.action.passwordQuarantineDesc')}
          </Text>
        </Flex>
      )}
      {item.threatType === t('policy.threatType.suspiciousSender') &&
        (item.action === ActionEnum.quarantine ||
          item.action === ActionEnum.delete) && (
          <Flex alignItems="center">
            <Text color={colorStyle.color.secondary} width="304px">
              {t('policy.threatType.suspiciousSender.node')}
            </Text>
          </Flex>
        )}
    </Stack>
  );
};

const ActionNotification = (props: { item: ActionData }) => {
  const { item } = props;
  const { t } = useTranslation();
  // Request password from end user forces notification on, the recipient
  // has to be notified to supply the password
  const notifyLockedOn = item.action === ActionEnum.passwordQuarantine;
  const isNotifyOn = !!item.notifyType || notifyLockedOn;

  return (
    <Switch
      value={item.notifyType}
      checked={isNotifyOn}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
        item.handler({
          [item.notifyTypeKey]: Number(e.target.checked),
        });
      }}
      disabled={item.action === ActionEnum.passWithoutLogging || notifyLockedOn}
    >
      {isNotifyOn ? t(`policy.status.on`) : t(`policy.status.off`)}
    </Switch>
  );
};

const ActionOtherSettings = (props: { item: ActionData }) => {
  const { item } = props;

  if (!item.otherSettings) {
    return null;
  }

  return (
    <Checkbox
      value={item.otherSettings.value}
      checked={!!item.otherSettings.value}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
        if (item.otherSettings) {
          item.handler({
            [item.otherSettings.otherSettingKey]: Number(e.target.checked),
          });
        }
      }}
      disabled={!!item.otherSettings.disabled}
    >
      <TextEllipsis lines={2}>{item.otherSettings.title}</TextEllipsis>
    </Checkbox>
  );
};

const TagSubjectInput = (props: { item: ActionData }) => {
  const { item } = props;
  return (
    <FormInput
      width="100%"
      value={item.suspiciousTag}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
        console.log(item.threatType);
        if (item.suspiciousTagKey) {
          item.handler({ [item.suspiciousTagKey]: e.target.value });
        }
      }}
      disabled={
        typeof item.actionTypeDisabled !== 'undefined'
          ? !!item.actionTypeDisabled
          : false
      }
      maxLength={256}
    />
  );
};

const DisclaimerInput = (props: { item: ActionData }) => {
  const { item } = props;
  return (
    <FormTextarea
      width="100%"
      value={item.addDisclaimerValue}
      onChange={(e) => {
        if (item.addDisclaimerKey) {
          item.handler({ [item.addDisclaimerKey]: e.target.value });
        }
      }}
      disabled={
        typeof item.actionTypeDisabled !== 'undefined'
          ? !!item.actionTypeDisabled
          : false
      }
    />
  );
};
